"use client";

import { useRef, useState } from "react";
import {
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import { DstTriangle } from "@/components/tools/physics-mechanics/dst-triangle";
import { QuantityField } from "@/components/tools/physics-mechanics/quantity-field";
import {
  FAMILIES,
  type FormState,
  readSingle,
  SingleResults,
  SLOTS,
  type Slot,
  showValue,
  type TimeEntry,
} from "@/components/tools/physics-mechanics/speed-single";
import {
  TripInputs,
  type TripOutputUnits,
  TripResults,
  type TripRow,
} from "@/components/tools/physics-mechanics/speed-trip";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { SIG_FIG_CHOICES } from "@/lib/tool-kit/physics-mechanics/format";
import {
  type SdtUnknown,
  solveSdt,
} from "@/lib/tool-kit/physics-mechanics/speed-distance-time";
import { unitOptions } from "@/lib/tool-kit/physics-mechanics/units";

/** English strings. A translated content file overrides these through `ui`. */
export const SPEED_DISTANCE_TIME_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeSingle: "Speed, distance or time",
  modeTrip: "Average speed of a trip with several legs",
  solveFor: "Find the",
  findSpeed: "Speed",
  findDistance: "Distance",
  findTime: "Time",
  distance: "Distance",
  speed: "Speed",
  time: "Time",
  unitFor: "Unit for {name}",
  calculated: "Calculated",
  timeEntry: "Enter the time as",
  timeSingle: "One value",
  timeHms: "Hours, minutes and seconds",
  hours: "Hours",
  minutes: "Minutes",
  seconds: "Seconds",
  hoursShort: "h",
  minutesShort: "min",
  secondsShort: "s",
  rounding: "Round answers to",
  sigFigs: "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  timeAs: "Time in hours, minutes and seconds",
  hmsLine: "Total time: {time}.",
  equivalentHeading: "The speed in other units",
  columnUnit: "Unit",
  columnValue: "Speed",
  pacePerKm: "min per km (pace)",
  pacePerMile: "min per mile (pace)",
  copy: "Copy result",
  copied: "Copied!",
  workingHeading: "The working",
  stepSi: "Convert to SI units: {list}.",
  stepFormulaSpeed: "Speed = distance ÷ time.",
  stepFormulaDistance: "Distance = speed × time.",
  stepFormulaTime: "Time = distance ÷ speed.",
  workSpeed: "Speed = {distance} ÷ {time} = {result}",
  workDistance: "Distance = {speed} × {time} = {result}",
  workTime: "Time = {distance} ÷ {speed} = {result}",
  stepConvert: "In the unit you chose: {si} = {shown}.",
  triangleTitle:
    "Speed, distance and time triangle: distance on top, speed and time underneath. The quantity being found is shaded.",
  triangleCaption: "Cover the one you want: D = S × T, S = D ÷ T, T = D ÷ S.",
  triangleD: "D",
  triangleS: "S",
  triangleT: "T",
  assumptions:
    "Treats speed as constant over the journey. For a trip with changing speeds, use the several-legs mode, which gives the true average.",
  legLabel: "Leg {n}",
  legDistance: "Distance of leg {n}",
  legKind: "Leg {n} is described by",
  legValueCaption: "Speed or time",
  kindSpeed: "Speed",
  kindTime: "Time",
  legValueSpeed: "Speed on leg {n}",
  legValueTime: "Time taken by leg {n}",
  addLeg: "Add a leg",
  removeLeg: "Remove leg {n}",
  showDistanceIn: "Show distances in",
  showSpeedIn: "Show speeds in",
  showTimeIn: "Show times in",
  tripHeading: "Leg",
  columnLeg: "Leg {n}",
  columnDistance: "Distance",
  columnSpeed: "Speed",
  columnTime: "Time",
  columnTotal: "Whole trip",
  totalDistance: "Total distance",
  totalTime: "Total time",
  averageSpeed: "Average speed",
  tripFormula:
    "Average speed = total distance ÷ total time = {distance} ÷ {time} = {speed}.",
  meanNote:
    "The plain mean of the leg speeds is {mean}, which is not the average speed ({average}): the slow legs take more time, so they count for more.",
  tripUnreadable:
    "Enter a distance and a speed or time for leg {n} as plain numbers.",
  tripErrors: {
    "no-legs": "Add at least one leg.",
    negative:
      "Leg {n} has a negative number. Distances, speeds and times cannot be negative here.",
    "zero-speed":
      "Leg {n} has a speed of zero, so it never covers its distance. Give a speed above zero, or describe the leg by its time.",
    "zero-time":
      "Leg {n} takes no time, so its speed is undefined. Give a time above zero.",
    "zero-total-time":
      "The whole trip takes no time, so there is no average speed.",
  },
  enterAll: "Enter the other two values to see the result.",
  unreadable:
    "{name} is not a number. Use digits and a decimal point, for example 12.5.",
  errors: {
    "negative-distance": "Distance cannot be negative.",
    "negative-speed":
      "Speed cannot be negative — speed has no direction. Use a velocity calculation if direction matters.",
    "negative-time": "Time cannot be negative.",
    "zero-time":
      "Time is zero, so speed cannot be found. Enter a time above zero.",
    "zero-speed":
      "Speed is zero, so the distance is never covered and the time is unlimited. Enter a speed above zero.",
  },
} as const;

type Mode = "single" | "trip";

const INITIAL_TRIP: TripRow[] = [
  {
    id: 1,
    distance: "60",
    distanceUnit: "km",
    kind: "speed",
    value: "80",
    speedUnit: "kph",
    timeUnit: "h",
  },
  {
    id: 2,
    distance: "60",
    distanceUnit: "km",
    kind: "speed",
    value: "40",
    speedUnit: "kph",
    timeUnit: "h",
  },
];

interface Entry {
  value: string;
  unit: string;
}

type Entries = Record<Slot, Entry>;

const INITIAL: Entries = {
  distance: { value: "150", unit: "km" },
  speed: { value: "", unit: "kph" },
  time: { value: "2.5", unit: "h" },
};

export default function SpeedDistanceTimeCalculatorWidget() {
  const strings = useToolUi(SPEED_DISTANCE_TIME_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [mode, setMode] = useState<Mode>("single");
  const [unknown, setUnknown] = useState<SdtUnknown>("speed");
  const [entries, setEntries] = useState<Entries>(INITIAL);
  const [timeEntry, setTimeEntry] = useState<TimeEntry>("single");
  const [hms, setHms] = useState({ h: "2", m: "30", s: "" });
  const [rows, setRows] = useState<TripRow[]>(INITIAL_TRIP);
  const [units, setUnits] = useState<TripOutputUnits>({
    distance: "km",
    speed: "kph",
    time: "h",
  });
  const [sigFigs, setSigFigs] = useState("4");
  const nextId = useRef(10);
  const sig = Number(sigFigs);
  const form: FormState = { unknown, entries, timeEntry, hms };
  const reading = readSingle(unknown, entries, timeEntry, hms);
  const solved =
    reading.missing || reading.unreadable
      ? null
      : solveSdt(unknown, reading.known);
  const patch = (slot: Slot, change: Partial<Entry>) =>
    setEntries({ ...entries, [slot]: { ...entries[slot], ...change } });
  const labels = {
    title: strings.triangleTitle,
    distance: strings.triangleD,
    speed: strings.triangleS,
    time: strings.triangleT,
    caption: strings.triangleCaption,
  };

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="sdt-mode"
        onChange={setMode}
        options={[
          { value: "single", label: strings.modeSingle },
          { value: "trip", label: strings.modeTrip },
        ]}
        value={mode}
      />
      {mode === "single" ? (
        <>
          <PillGroup
            label={strings.solveFor}
            name="sdt-unknown"
            onChange={setUnknown}
            options={[
              { value: "speed", label: strings.findSpeed },
              { value: "distance", label: strings.findDistance },
              { value: "time", label: strings.findTime },
            ]}
            value={unknown}
          />
          <div className="grid items-start gap-6 sm:grid-cols-[1fr_auto]">
            <div className="grid gap-4">
              {SLOTS.map((slot) => {
                const answer =
                  slot === unknown && solved?.ok === true
                    ? showValue(
                        slot,
                        solved[slot],
                        entries[slot].unit,
                        fmt,
                        sig
                      )
                    : null;
                if (
                  slot === "time" &&
                  unknown !== "time" &&
                  timeEntry === "hms"
                ) {
                  return (
                    <fieldset className="space-y-2" key={slot}>
                      <legend className={LABEL}>{strings.time}</legend>
                      <div className="grid grid-cols-3 gap-2">
                        {(["h", "m", "s"] as const).map((part) => (
                          <TextField
                            id={`sdt-hms-${part}`}
                            key={part}
                            label={
                              {
                                h: strings.hours,
                                m: strings.minutes,
                                s: strings.seconds,
                              }[part]
                            }
                            numeric
                            onChange={(value) =>
                              setHms({ ...hms, [part]: value })
                            }
                            value={hms[part]}
                          />
                        ))}
                      </div>
                    </fieldset>
                  );
                }
                return (
                  <QuantityField
                    calculated={answer}
                    hint={answer === null ? undefined : strings.calculated}
                    id={`sdt-${slot}`}
                    invalid={reading.unreadable === slot}
                    key={slot}
                    label={strings[slot]}
                    onUnit={(unit) => patch(slot, { unit })}
                    onValue={(value) => patch(slot, { value })}
                    unit={entries[slot].unit}
                    unitLabel={t(strings.unitFor, { name: strings[slot] })}
                    units={unitOptions(FAMILIES[slot])}
                    value={entries[slot].value}
                  />
                );
              })}
              {unknown === "time" ? null : (
                <SelectField
                  id="sdt-time-entry"
                  label={strings.timeEntry}
                  onChange={setTimeEntry}
                  options={[
                    { value: "single", label: strings.timeSingle },
                    { value: "hms", label: strings.timeHms },
                  ]}
                  value={timeEntry}
                />
              )}
            </div>
            <DstTriangle labels={labels} unknown={unknown} />
          </div>
        </>
      ) : (
        <TripInputs
          nextId={() => {
            nextId.current += 1;
            return nextId.current;
          }}
          onRows={setRows}
          onUnits={setUnits}
          rows={rows}
          strings={strings}
          units={units}
        />
      )}
      <div className="w-60">
        <SelectField
          id="sdt-sig"
          label={strings.rounding}
          onChange={setSigFigs}
          options={SIG_FIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigFigs, locale, Number(choice), {
              count: fmt(Number(choice)),
            }),
          }))}
          value={sigFigs}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "single" ? (
          <SingleResults fmt={fmt} form={form} sig={sig} strings={strings} />
        ) : (
          <TripResults
            fmt={fmt}
            rows={rows}
            sig={sig}
            strings={strings}
            units={units}
          />
        )}
      </section>
    </>
  );
}
